import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import { isAdmin } from '../../../helpers/roleHelpers';
import CourseTrainingEditor from './CourseTrainingEditor';
import CourseTrainingCatalog from './CourseTrainingCatalog';

export const COURSE_TRAINING_VIEW_BASE = '/sales-academy';
export const COURSE_TRAINING_MANAGE_BASE = '/admin/manage-sales-academy';

function CourseTrainingIndex() {
  const location = useLocation();
  const history = useHistory();
  const { courseId: courseIdParam } = useParams();
  const [courses, setCourses] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listSearchInput, setListSearchInput] = useState('');

  const userCanManageCourses = useMemo(() => isAdmin(), []);
  const isManageRoute = location.pathname.startsWith(COURSE_TRAINING_MANAGE_BASE);
  const canManageCourses = userCanManageCourses && isManageRoute;
  const playerCourseId = courseIdParam ? String(courseIdParam) : null;
  const courseViewOnly = Boolean(playerCourseId) && !isManageRoute;
  const loadCourses = useCallback(async searchQuery => {
    setListLoading(true);
    const q = String(searchQuery ?? '').trim();
    const listRes = await makeApiRequests({
      endpoint: ENDPOINTS.COURSES_SEARCH,
      requestBody: {
        sort: { order: 1 },
        ...(q ? { query: q } : {})
      }
    });
    if (listRes.error) {
      setListLoading(false);
      toast.error(listRes.error);
      return;
    }
    const raw = listRes.response.results || [];
    setListLoading(false);
    setCourses(
      raw.map(c => ({
        ...c,
        modules: [],
        moduleCount: c.moduleCount ?? 0,
        lessonCount: c.lessonCount ?? 0,
        allowedRoles: c.allowedRoles || []
      }))
    );
  }, []);

  const mergeCourseDetail = useCallback(fullCourse => {
    const fullId =
      fullCourse == null ? '' : String(fullCourse._id ?? fullCourse.id ?? '');
    if (!fullId) return;
    const lessonCount = fullCourse?.modules?.length
      ? fullCourse.modules.reduce((a, m) => a + (m.lessons?.length || 0), 0)
      : 0;
    setCourses(prev => {
      const i = prev.findIndex(c => String(c._id ?? c.id ?? '') === fullId);
      if (i === -1) {
        return [
          ...prev,
          {
            ...fullCourse,
            moduleCount: fullCourse.modules?.length ?? 0,
            lessonCount
          }
        ];
      }
      const next = [...prev];
      next[i] = {
        ...next[i],
        ...fullCourse,
        moduleCount: fullCourse.modules?.length ?? next[i].moduleCount,
        lessonCount
      };
      return next;
    });
  }, []);

  const exitCoursePlayer = useCallback(() => {
    history.push(isManageRoute ? COURSE_TRAINING_MANAGE_BASE : COURSE_TRAINING_VIEW_BASE);
  }, [history, isManageRoute]);

  const openManageCourse = useCallback(
    id => {
      const courseId = String(id || '').trim();
      if (!courseId) return;
      history.push(`${COURSE_TRAINING_MANAGE_BASE}/${courseId}`);
    },
    [history]
  );

  const openViewCourse = useCallback(
    id => {
      const courseId = String(id || '').trim();
      if (!courseId) return;
      history.push(`${COURSE_TRAINING_VIEW_BASE}/${courseId}`);
    },
    [history]
  );

  useEffect(() => {
    const q = listSearchInput.trim();
    const delay = q === '' ? 0 : 350;
    const t = window.setTimeout(() => loadCourses(q), delay);
    return () => window.clearTimeout(t);
  }, [listSearchInput, loadCourses]);

  return (
    <div className="position-relative">
      {playerCourseId != null ? (
        <CourseTrainingEditor
          courseId={playerCourseId}
          viewOnly={courseViewOnly}
          courses={courses}
          canManageCourses={canManageCourses}
          onCourseDetailLoaded={mergeCourseDetail}
          onExitEditor={exitCoursePlayer}
        />
      ) : (
        <CourseTrainingCatalog
          courses={courses}
          setCourses={setCourses}
          canManageCourses={canManageCourses}
          listLoading={listLoading}
          listSearchInput={listSearchInput}
          onListSearchInputChange={setListSearchInput}
          onReloadList={() => loadCourses(listSearchInput.trim())}
          onManageCourse={openManageCourse}
          onViewCourse={openViewCourse}
        />
      )}
    </div>
  );
}

export default CourseTrainingIndex;
